import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Line, Pie } from 'react-chartjs-2';
import { SHOWS } from '../config/shows';

const ShowAnalytics = () => {
  const [selectedShow, setSelectedShow] = useState('');
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [showData, setShowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedShow && selectedDate) {
      fetchShowData();
    }
  }, [selectedShow, selectedDate]);

  const fetchShowData = async () => {
    setLoading(true);
    setError(null);

    try {
      const show = SHOWS.find(s => s.id === selectedShow);
      if (!show) return;

      // Fetch full day data
      const response = await axios.get('/api/historical', {
        params: {
          startDate: selectedDate,
          endDate: selectedDate,
          _t: Date.now()
        }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Filter data for show time slot
        const startHour = parseInt(show.startTime.split(':')[0]);
        const endHour = parseInt(show.endTime.split(':')[0]);
        
        // Filter hourly data for show time
        const showHourly = (data.analytics?.hourly || []).filter(h => 
          h.hour >= startHour && h.hour < endHour
        );

        // Calculate show-specific metrics
        const peakListeners = Math.max(...showHourly.map(h => h.listeners || 0), 0);
        const avgListeners = showHourly.length > 0 
          ? Math.round(showHourly.reduce((sum, h) => sum + (h.listeners || 0), 0) / showHourly.length)
          : 0;
        
        setShowData({
          show,
          date: selectedDate,
          hourlyData: showHourly,
          peakListeners,
          avgListeners,
          geographic: data.analytics?.geographic || [],
          totalHours: endHour - startHour
        });
      } else {
        setShowData(null);
      }
    } catch (err) {
      console.error('Error fetching show data:', err);
      setError('Failed to load show data');
    } finally {
      setLoading(false);
    }
  };

  const prepareHourlyChart = () => {
    if (!showData || !showData.hourlyData.length) return null;

    const show = showData.show;
    const startHour = parseInt(show.startTime.split(':')[0]);
    const endHour = parseInt(show.endTime.split(':')[0]);
    const hours = [];
    
    for (let h = startHour; h < endHour; h++) {
      hours.push(h);
    }

    const hourlyMap = new Map();
    showData.hourlyData.forEach(item => {
      hourlyMap.set(item.hour, item.listeners || 0);
    });

    return {
      labels: hours.map(h => `${h.toString().padStart(2, '0')}:00`),
      datasets: [{
        label: 'Listeners',
        data: hours.map(h => hourlyMap.get(h) || 0),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
        fill: true,
        borderWidth: 3
      }]
    };
  };

  const prepareGeoChart = () => {
    if (!showData || !showData.geographic.length) return null;

    const colors = [
      'rgba(239, 68, 68, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(34, 197, 94, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(147, 51, 234, 0.8)',
      'rgba(236, 72, 153, 0.8)',
      'rgba(14, 165, 233, 0.8)',
      'rgba(168, 85, 247, 0.8)',
      'rgba(251, 146, 60, 0.8)',
      'rgba(20, 184, 166, 0.8)'
    ];

    return {
      labels: showData.geographic.map(item => item.country),
      datasets: [{
        label: 'Listeners by Country',
        data: showData.geographic.map(item => item.listeners),
        backgroundColor: colors,
        borderWidth: 1
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: { color: '#e5e7eb' }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(156, 163, 175, 0.2)' }
      },
      y: {
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(156, 163, 175, 0.2)' },
        beginAtZero: true
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { 
          color: '#e5e7eb', 
          padding: 10,
          font: { size: 11 }
        }
      }
    }
  };

  const hourlyChart = prepareHourlyChart();
  const geoChart = prepareGeoChart();

  return (
    <div className="show-analytics-section">
      <div className="show-analytics-header">
        <h3>📺 Show-Specific Analytics</h3>
        <p className="show-analytics-subtitle">
          Analyze performance metrics for individual shows during their broadcast time
        </p>
      </div>

      <div className="show-selector-card">
        <div className="show-selector-row">
          <div className="show-select-group">
            <label>Select Show:</label>
            <select
              value={selectedShow}
              onChange={(e) => setSelectedShow(e.target.value)}
              className="show-select-input"
            >
              <option value="">-- Choose a Show --</option>
              {SHOWS
                .sort((a, b) => {
                  const nameA = a.host ? `${a.host} - ${a.name}` : a.name;
                  const nameB = b.host ? `${b.host} - ${b.name}` : b.name;
                  return nameA.localeCompare(nameB);
                })
                .map(show => (
                  <option key={show.id} value={show.id}>
                    {show.host ? `${show.host} - ` : ''}{show.name} ({show.days[0]} {show.startTime} - {show.endTime})
                  </option>
                ))}
            </select>
          </div>

          {selectedShow && (
            <div className="show-date-group">
              <label>Select Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="show-date-input"
                max={moment().format('YYYY-MM-DD')}
              />
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="show-loading">
          <div className="spinner"></div>
          <p>Loading show analytics...</p>
        </div>
      )}

      {error && (
        <div className="show-error">
          <p>⚠️ {error}</p>
        </div>
      )}

      {showData && !loading && (
        <div className="show-analytics-card">
          <div className="show-card-header">
            <h4>
              🎙️ {showData.show.name} - {moment(showData.date).format('dddd, MMMM DD, YYYY')}
            </h4>
            <div className="show-meta">
              <p className="show-time">
                ⏰ {showData.show.startTime} - {showData.show.endTime} ({showData.totalHours} hour{showData.totalHours !== 1 ? 's' : ''})
              </p>
              {showData.show.genre && (
                <p className="show-genre">🎵 {showData.show.genre}</p>
              )}
              {showData.show.host && (
                <p className="show-host">🎧 Hosted by {showData.show.host}</p>
              )}
            </div>
          </div>

          <div className="show-quick-stats">
            <div className="show-stat-box">
              <span className="show-stat-icon">🔥</span>
              <div>
                <div className="show-stat-label">Peak Listeners</div>
                <div className="show-stat-value">{showData.peakListeners}</div>
              </div>
            </div>
            <div className="show-stat-box">
              <span className="show-stat-icon">📊</span>
              <div>
                <div className="show-stat-label">Avg Listeners</div>
                <div className="show-stat-value">{showData.avgListeners}</div>
              </div>
            </div>
            <div className="show-stat-box">
              <span className="show-stat-icon">🌍</span>
              <div>
                <div className="show-stat-label">Countries Reached</div>
                <div className="show-stat-value">{showData.geographic.length}</div>
              </div>
            </div>
          </div>

          {hourlyChart && (
            <div className="show-chart-section">
              <h5>📈 Listeners During Show</h5>
              <div className="show-chart-container">
                <Line data={hourlyChart} options={chartOptions} />
              </div>
            </div>
          )}

          {geoChart && (
            <div className="show-geo-section">
              <div className="show-geo-chart">
                <h5>🌍 Geographic Distribution</h5>
                <div className="show-pie-container">
                  <Pie data={geoChart} options={pieOptions} />
                </div>
              </div>

              <div className="show-geo-list">
                <h5>📍 All Listening Locations</h5>
                <div className="show-geo-table">
                  <div className="show-geo-header">
                    <span>Rank</span>
                    <span>Country/City</span>
                    <span>Listeners</span>
                  </div>
                  {showData.geographic.map((geo, idx) => (
                    <div key={idx} className="show-geo-row">
                      <span className="show-geo-rank">#{idx + 1}</span>
                      <span className="show-geo-name">{geo.country}</span>
                      <span className="show-geo-count">{geo.listeners}</span>
                    </div>
                  ))}
                  {showData.geographic.length === 0 && (
                    <div className="show-no-data">
                      No geographic data available for this show time
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedShow && !loading && (
        <div className="show-empty-state">
          <div className="show-empty-icon">📺</div>
          <h4>Select a Show to Begin</h4>
          <p>Choose a show from the dropdown above to view its performance analytics</p>
        </div>
      )}
    </div>
  );
};

export default ShowAnalytics;

